import React, { useEffect, useState } from "react";
import { memberApi } from "@/lib/memberAuth";
import { toast } from "sonner";
import { DownloadSimple, FilePdf, FileZip, FileText } from "@phosphor-icons/react";

const iconFor = (url) => {
  const u = (url || "").toLowerCase();
  if (u.endsWith(".pdf")) return FilePdf;
  if (u.endsWith(".zip") || u.endsWith(".rar")) return FileZip;
  return FileText;
};

export default function MemberDownloads() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    memberApi.get("/portal/downloads")
      .then(({ data }) => setItems(data.downloads || []))
      .catch(() => toast.error("Could not load downloads"))
      .finally(() => setLoading(false));
  }, []);

  const grouped = items.reduce((acc, d) => {
    (acc[d.category] = acc[d.category] || []).push(d);
    return acc;
  }, {});

  return (
    <div data-testid="portal-downloads">
      <header className="mb-6">
        <div className="overline mb-1">Portal</div>
        <h1 className="font-display text-3xl font-bold text-navy tracking-tight flex items-center gap-2">
          <DownloadSimple size={24} weight="duotone" className="text-ember" /> Downloads
        </h1>
        <p className="text-sm text-navy/70 mt-2 max-w-2xl">
          Member-only kit — brochures, contracts, EPAS logos, corridor infographics. Everything you might need
          to introduce EPAS to a client or partner.
        </p>
      </header>

      {loading ? (
        <div className="border border-black/10 bg-white p-8 text-center text-navy/50 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-black/10 bg-white p-8 text-center text-navy/50 text-sm">
          No downloads yet. EPAS operations will upload the kit shortly.
        </div>
      ) : (
        Object.entries(grouped).map(([cat, list]) => (
          <section key={cat} className="mb-8" data-testid={`downloads-category-${cat}`}>
            <div className="overline mb-3">{cat.replace(/_/g, " ")}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {list.map((d) => {
                const Icon = iconFor(d.url);
                return (
                  <a key={d.id} href={d.url} target="_blank" rel="noopener noreferrer" data-testid={`download-${d.id}`}
                     className="border border-black/10 bg-white p-4 hover:border-navy transition-colors group flex items-start gap-3">
                    <Icon size={22} weight="duotone" className="text-ember shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="font-medium text-navy truncate">{d.title}</div>
                      {d.description && <div className="text-xs text-navy/60 mt-1 line-clamp-2">{d.description}</div>}
                      {d.size_note && <div className="text-[10px] uppercase tracking-widest text-navy/40 mt-2">{d.size_note}</div>}
                    </div>
                  </a>
                );
              })}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
